import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useSearchableCourses } from '../../hooks/useSearchableCourses';
import { useI18n } from '../../i18n/I18nContext';
import { popoverRoot, useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import './SearchableCourseSelect.css';

const LEVEL_KEYS = {
  BEGINNER: 'ui.levelBeginner',
  INTERMEDIATE: 'ui.levelIntermediate',
  ADVANCED: 'ui.levelAdvanced',
};

/**
 * Searchable, paginated course dropdown component (a `.ui-select` trigger and a portaled
 * `.ui-menu` card with a search field, spec §3.9). Loads the first 20 courses, loads more
 * on scroll. Keyboard: type to search, Arrow keys move, Enter picks, Esc closes.
 */
export default function SearchableCourseSelect({
  value,
  onChange,
  placeholder,
  disabled = false,
  size = 'md',
}) {
  const { t } = useI18n();
  const uid = useId();
  const resolvedPlaceholder = placeholder ?? t('ui.searchCoursesPlaceholder');
  const {
    courses,
    loading,
    error,
    search,
    setSearch,
    hasMore,
    loadMore
  } = useSearchableCourses({ limit: 20 });

  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const pos = useAnchoredPopover(isOpen, setIsOpen, anchorRef, popRef, { matchWidth: true });
  // The list follows the search box, so the course that was picked may drop out of it: keep it
  // for the button label.
  const [pickedCourse, setPickedCourse] = useState(null);

  const handleSelectCourse = (course) => {
    setPickedCourse(course);
    onChange(course.id);
    setIsOpen(false);
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  const handleScroll = () => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom && hasMore && !loading) {
      loadMore();
    }
  };

  const positioned = Boolean(pos);
  useLayoutEffect(() => {
    if (isOpen && positioned && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [isOpen, positioned]);

  useEffect(() => {
    if (!isOpen || active < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${active}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [isOpen, active]);

  const openMenu = () => {
    if (disabled) return;
    setActive(Math.max(0, courses.findIndex((c) => c.id === value)));
    setIsOpen(true);
  };

  const onSearchKeyDown = (e) => {
    const last = courses.length - 1;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i >= last ? 0 : i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i <= 0 ? last : i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (courses[active]) handleSelectCourse(courses[active]); }
    else if (e.key === 'Tab') setIsOpen(false);
  };

  const selectedCourse = courses.find(c => c.id === value)
    || (pickedCourse && pickedCourse.id === value ? pickedCourse : null);

  const menu = isOpen ? (
    <div
      ref={popRef}
      className="ui-menu scs-menu"
      style={pos
        ? { top: pos.top, left: pos.left, minWidth: pos.minWidth, zIndex: pos.zIndex }
        : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
    >
      <div className="ui-menu__search">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={`${uid}-list`}
          aria-activedescendant={active >= 0 && courses[active] ? `${uid}-opt-${active}` : undefined}
          placeholder={t('ui.searchCoursesPlaceholder')}
          aria-label={t('ui.searchCoursesPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setActive(0); }}
          onKeyDown={onSearchKeyDown}
          autoComplete="off"
        />
      </div>

      {error && (
        <div className="ui-alert scs-error" role="alert">
          <div>{t('ui.coursesLoadFailed')}</div>
        </div>
      )}

      <div
        id={`${uid}-list`}
        role="listbox"
        className="ui-menu__list"
        ref={listRef}
        onScroll={handleScroll}
      >
        {courses.map((course, i) => {
          const isSelected = value === course.id;
          const levelKey = LEVEL_KEYS[String(course.level || '').toUpperCase()];
          return (
            <div
              key={course.id}
              id={`${uid}-opt-${i}`}
              data-index={i}
              role="option"
              aria-selected={isSelected}
              className={`ui-menu__item${isSelected ? ' is-selected' : ''}${i === active ? ' is-focused' : ''}`}
              onMouseMove={() => { if (active !== i) setActive(i); }}
              onClick={() => handleSelectCourse(course)}
            >
              <span>{course.title}</span>
              {course.level && (
                <span className="ds-badge scs-level">{levelKey ? t(levelKey) : course.level}</span>
              )}
              {isSelected && <Check className="ui-menu__check" aria-hidden="true" />}
            </div>
          );
        })}

        {courses.length === 0 && !loading && (
          <div className="ui-menu__empty">{t('ui.noCoursesFound')}</div>
        )}

        {loading && <div className="ui-menu__empty">{t('ui.loading')}</div>}

        {hasMore && !loading && (
          <button type="button" className="ui-menu__more" onClick={loadMore}>
            {t('ui.loadMoreCourses')}
          </button>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="searchable-course-select" ref={anchorRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`scs-trigger ui-select${size === 'sm' ? ' ui-select--sm' : ''}${selectedCourse ? '' : ' is-placeholder'}`}
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="ui-select__label">{selectedCourse ? selectedCourse.title : resolvedPlaceholder}</span>
        <ChevronDown className="ui-select__chevron" aria-hidden="true" />
      </button>
      {menu && createPortal(menu, popoverRoot(anchorRef.current))}
    </div>
  );
}
